import {
  Button,
  Flex,
  Text,
  Menu,
  MenuButton,
  MenuList,
  Divider,
  useMediaQuery,
} from '@chakra-ui/react';
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ChevronDownIcon } from '@chakra-ui/icons';

import MultiSelectCheckBox from 'components/MultiSelectCheckBox';
import {
  PlanToolbarChevronDownIcon,
  PlanToolbarFilterIcon,
} from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/icons';
import { PLAN_TEXT } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/planPageRevampUi';
import { usePlanPageRevampVariant } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/usePlanPageUiRevamp';
import {
  // fetchAnalysisSectionData,
  FetchBlockItemDimensions,
  getBlockDimensions,
  getDimension,
  getSelectedBlockDimension,
  handleFilters,
  overallGroupByFilter,
  overallIndicatorFilter,
  overallTimeFilter,
  resetSelectedBlockDimension,
  setMultiDimFilter,
  setFilterItems,
  getIndicatorTableData,
  // fetchTableData,
  getAppliedFilters,
  fetchAnalysisSectionDataVersion2,
  filterStartDate,
  filterEndDate,
  getBlockDetails,
  setSelectedBlockDimensionDirect,
} from 'redux/PlanPageSlice';
import {
  getBaseScenario as planPageBaseScenario,
  getCompareScenario,
  fetchCompareTableData,
  fetchCompareAnalysisSectionData,
} from 'redux/ScenarioSlice';
import { GetInputAdjustments } from 'redux/driverAdjustmentSlice';
import { indicatorfiltersName, getTableFilterPayload } from 'utils/GlobalHelpers';
import {
  fetchIndicatorDetail,
  fetchIndicatorDetailType,
  getIndicatorDetail,
} from 'redux/IndicatorPageSlice';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';

import ErrorBoundary from '../../../../components/ErrorBoundary';

/**
 * MultipleIndicatorFilters Component
 *
 * This component includes error handling for:
 * 1. Undefined indicatorTableData or column_headers
 * 2. Safe access to nested properties with try-catch
 * 3. ErrorBoundary wrapper for React error catching
 * 4. Early return with loading state when data is not available
 */
interface MultipleIndicatorFiltersProperties {
  variant?: 'legacy' | 'revamp';
  embedded?: boolean;
  badgeColor?: string;
  onApplyFilters?: () => void;
}

const MultipleIndicatorFilters = ({
  variant = 'legacy',
  embedded = false,
  badgeColor,
  onApplyFilters,
}: MultipleIndicatorFiltersProperties) => {
  const { blockId } = useParams();
  const dispatch: any = useDispatch();

  const [clearButton, setClearButton] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const indicatorTableData = useSelector(getIndicatorTableData);
  const blockDimensions = useSelector(getBlockDimensions);
  const compareScenario = useSelector(getCompareScenario);
  const indicator = useSelector(getIndicatorDetail);
  const selectedBlockDimension = useSelector(getSelectedBlockDimension);
  const dimension = useSelector(getDimension);
  const selectedYear = useSelector(overallTimeFilter);
  const selectedGroupBy = useSelector(overallGroupByFilter);
  const baseScenario = useSelector(planPageBaseScenario);
  const inputAdjustments = useSelector(GetInputAdjustments);
  const indicatorFilter = useSelector(overallIndicatorFilter);
  const appliedFilters = useSelector(getAppliedFilters);
  const [isChartFilter, setIsChartFilter] = useState<any>(false);
  const startDatePlanPage = useSelector(filterStartDate);
  const endDatePlanPage = useSelector(filterEndDate);
  const dateFilterSelected = startDatePlanPage && endDatePlanPage;
  const dateFilter = `[{ 'start_date': '${startDatePlanPage}', 'end_date': '${endDatePlanPage}' }]`;
  const blockDetails = useSelector(getBlockDetails);
  const themeColors = useSelector(getPlanPageThemeColors);
  const isRevamp = usePlanPageRevampVariant(variant);

  const activeFilterCount = useMemo(() => {
    const source =
      appliedFilters && appliedFilters.length > 0 ? appliedFilters : selectedBlockDimension;
    if (!Array.isArray(source)) return 0;
    return source.reduce((count: number, item: any) => {
      const values = Object.values(item)[0];
      return count + (Array.isArray(values) ? values.length : 0);
    }, 0);
  }, [appliedFilters, selectedBlockDimension]);

  const scenarioId = baseScenario?.id;

  // Safe access to chartFilterDimId with additional error handling
  let chartFilterDimId: any = null;
  try {
    chartFilterDimId = indicatorTableData?.column_headers?.[0]?.is_dimension
      ? indicatorTableData?.column_headers[0]?.id
      : null;
  } catch {
    chartFilterDimId = null;
  }
  const [isMobile] = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    dispatch(
      FetchBlockItemDimensions({
        blockId,
        parameters: {
          params: { scenario_id: baseScenario?.id },
        },
      }),
    );
  }, []);

  // Load filters from localStorage on mount
  useEffect(() => {
    if (blockDetails?.model_id && blockDetails?.id && blockDimensions?.dimensions?.length > 0) {
      try {
        const planFilters = localStorage.getItem('global-modal-filters');
        if (planFilters) {
          const data = JSON.parse(planFilters);
          const modalFilters = data[blockDetails.model_id]?.[blockDetails.id];
          if (modalFilters?.selectedBlockDimension) {
            // Restore the saved filter state
            dispatch(setSelectedBlockDimensionDirect(modalFilters.selectedBlockDimension));
          }
        }
      } catch {
        // Ignore invalid localStorage payload
      }
    }
  }, [blockDetails?.model_id, blockDetails?.id, blockDimensions?.dimensions, dispatch]);

  const fetchOutputData = () => {
    if (!compareScenario) {
      dispatch(
        fetchAnalysisSectionDataVersion2({
          blockId: Number(blockId),
          parameters: {
            dim_id:
              selectedGroupBy === 'Total'
                ? null
                : dimension && !selectedGroupBy
                ? dimension?.id
                : selectedGroupBy
                ? Number.parseInt(selectedGroupBy, 10)
                : null,
            dim_sort: 'ASC',
            time_filter: dateFilterSelected ? dateFilter : selectedYear || null,
            time_aggregation: 'Y,Q,M',
            indicator_filter: indicatorFilter,
            ...(inputAdjustments != null &&
              Object.keys(inputAdjustments).length > 0 && {
                input_adjustments: inputAdjustments,
              }),
            scenario_id: baseScenario?.id,
            dim_filter: JSON.stringify(selectedBlockDimension),
          },
        }),
      );
      // dispatch(
      //   fetchTableData({
      //     blockId: Number(blockId),
      //     parameters: {
      //       params: {
      //         dim_id:
      //           selectedGroupBy === 'Total'
      //             ? null
      //             : dimension && !selectedGroupBy
      //             ? dimension?.id
      //             : selectedGroupBy
      //             ? Number.parseInt(selectedGroupBy, 10)
      //             : null,
      //         dim_sort: 'ASC',
      //         time_filter: dateFilterSelected ? dateFilter : selectedYear || null,
      //         time_aggregation: 'Y,Q,M',
      //         indicator_filter: indicatorFilter,
      //         ...(Object.keys(inputAdjustments).length > 0 && {
      //           input_adjustments: inputAdjustments,
      //         }),
      //         scenario_id: baseScenario?.id,
      //         dim_filter: JSON.stringify(selectedBlockDimension),
      //       },
      //     },
      //   }),
      // );
    } else {
      dispatch(
        fetchCompareAnalysisSectionData({
          blockId: Number(blockId),
          parameters: {
            params: {
              dim_id:
                selectedGroupBy === 'Total'
                  ? null
                  : dimension && !selectedGroupBy
                  ? dimension?.id
                  : selectedGroupBy
                  ? Number.parseInt(selectedGroupBy, 10)
                  : null,
              dim_sort: 'ASC',
              time_filter: dateFilterSelected ? dateFilter : selectedYear || null,
              time_aggregation: 'Y,Q,M',
              indicator_filter: indicatorFilter,
              ...(inputAdjustments != null &&
                Object.keys(inputAdjustments).length > 0 && {
                  input_adjustments: inputAdjustments,
                }),
              scenario_id: baseScenario?.id,
              compare_scenario_id: compareScenario?.id,
              dim_filter: JSON.stringify(selectedBlockDimension),
            },
          },
        }),
      );
      dispatch(
        fetchCompareTableData({
          blockId: Number(blockId),
          parameters: {
            params: {
              dim_id:
                selectedGroupBy === 'Total'
                  ? null
                  : dimension && !selectedGroupBy
                  ? dimension?.id
                  : selectedGroupBy
                  ? Number.parseInt(selectedGroupBy, 10)
                  : null,
              dim_sort: 'ASC',
              time_filter: dateFilterSelected ? dateFilter : selectedYear || null,
              time_aggregation: 'Y,Q,M',
              indicator_filter: indicatorFilter,
              ...(inputAdjustments != null &&
                Object.keys(inputAdjustments).length > 0 && {
                  input_adjustments: inputAdjustments,
                }),
              scenario_id: baseScenario?.id,
              compare_scenario_id: compareScenario?.id,
              dim_filter: JSON.stringify(selectedBlockDimension),
            },
          },
        }),
      );
    }
  };

  useEffect(() => {
    if (clearButton) {
      if (onApplyFilters) {
        onApplyFilters();
      } else {
        fetchOutputData();
      }
    }
  }, [clearButton]);

  const applyIndicatorTableFilters = () => {
    const activeDimIds = indicatorTableData?.dimensions.map((item: any) => item.id);

    // eslint-disable-next-line unicorn/no-for-loop, unicorn/prevent-abbreviations, no-plusplus, no-lone-blocks, @typescript-eslint/no-unused-vars
    for (let i = 0; i < selectedBlockDimension.length; i++) {
      if (
        Object.values(selectedBlockDimension[i] as Record<string, any>)[0].length > 0 &&
        activeDimIds?.includes(String(blockDimensions?.dimensions[i].id))
      ) {
        const payload = getTableFilterPayload(
          selectedBlockDimension[i],
          blockDimensions?.dimensions[i],
        );
        dispatch(setFilterItems(payload.dimensionItems)); // for filter dropdown
        dispatch(setMultiDimFilter(payload.tableItems)); // for table items
      }
    }
  };

  useEffect(() => {
    if (appliedFilters && appliedFilters.length > 0 && isChartFilter && chartFilterDimId !== null) {
      dispatch(
        fetchIndicatorDetailType({
          currentDriver: indicator,
          parameters: {
            dim_id: chartFilterDimId,
            scenario_id: scenarioId,
            is_chart_show: true,
            filter_dim_items: JSON.stringify(appliedFilters),
          },
        }),
      ).then(() => {
        dispatch(
          fetchIndicatorDetail({
            currentDriver: indicator,
            parameters: {
              dim_id: chartFilterDimId,
              scenario_id: scenarioId,
              is_chart_show: true,
              filter_dim_items: JSON.stringify(appliedFilters),
            },
          }),
        );
      });
      setIsChartFilter(false);
    }
  }, [isChartFilter]);

  const handleApplyFilter = () => {
    setIsChartFilter(true);
    dispatch(handleFilters());
    setIsMenuOpen(false);

    // Save filter to localStorage
    if (blockDetails?.model_id && blockDetails?.id) {
      try {
        const planFilters = localStorage.getItem('global-modal-filters');
        const data: Record<string, any> = planFilters ? JSON.parse(planFilters) : {};
        const modelId = String(blockDetails.model_id);
        const currentBlockId = String(blockDetails.id);
        const modelData: Record<string, any> = (data as any)[modelId] || {};
        const blockFilters: any = modelData[currentBlockId] || {};
        blockFilters.selectedBlockDimension = selectedBlockDimension;
        modelData[currentBlockId] = blockFilters;
        (data as any)[modelId] = modelData;
        localStorage.setItem('global-modal-filters', JSON.stringify(data));
      } catch {
        // Ignore localStorage write failures
      }
    }

    if (onApplyFilters) {
      onApplyFilters();
    } else {
      fetchOutputData();
    }
    applyIndicatorTableFilters();
  };

  const handleClearAll = async () => {
    setIsMenuOpen(false);
    setClearButton(true);
    await dispatch(resetSelectedBlockDimension());

    // Clear filter from localStorage
    if (blockDetails?.model_id && blockDetails?.id) {
      try {
        const planFilters = localStorage.getItem('global-modal-filters');
        if (planFilters) {
          const data: Record<string, any> = JSON.parse(planFilters);
          const modelId = String(blockDetails.model_id);
          const currentBlockId = String(blockDetails.id);
          const modelData: Record<string, any> = (data as any)[modelId] || {};
          if (modelData[currentBlockId]) {
            delete modelData[currentBlockId].selectedBlockDimension;
            (data as any)[modelId] = modelData;
            localStorage.setItem('global-modal-filters', JSON.stringify(data));
          }
        }
      } catch {
        // Ignore localStorage clear failures
      }
    }

    setClearButton(false);
    if (onApplyFilters) {
      onApplyFilters();
    }
  };

  // if (!indicatorTableData || !indicatorTableData.column_headers || !chartFilterDimId) {
  //   return (
  //     <Flex align='center' justify='center' p={4}>
  //       <Text color='gray.500' fontSize='sm'>
  //         Loading filters...
  //       </Text>
  //     </Flex>
  //   );
  // }

  return (
    <ErrorBoundary>
      <Menu
        variant={'filter'}
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        onOpen={() => setIsMenuOpen(true)}
        placement='bottom-end'
        strategy='fixed'
      >
        <MenuButton
          as={Button}
          paddingRight={embedded ? undefined : '2'}
          paddingLeft={embedded ? undefined : '2'}
          px={embedded ? 0 : undefined}
          height={isMobile ? '25px' : isRevamp && embedded ? '32px' : isRevamp ? '36px' : '30px'}
          width='fit-content'
          textTransform='none'
          fontWeight={isRevamp ? '500' : '400'}
          fontSize={isMobile ? 'xs' : isRevamp ? '14px' : 'sm'}
          color={isRevamp ? themeColors.textDark : 'white'}
          border={isRevamp && !embedded ? '1px solid' : 'none'}
          borderColor={isRevamp && !embedded ? themeColors.border : undefined}
          borderRadius={isRevamp && !embedded ? '10px' : embedded ? '0' : undefined}
          bg={isRevamp ? 'transparent' : 'transaparant'}
          boxShadow='none'
          display={embedded ? 'inline-flex' : undefined}
          alignItems={embedded ? 'center' : undefined}
          gap={embedded ? 3 : undefined}
          lineHeight={embedded ? '1' : undefined}
          _hover={{
            bg: isRevamp ? themeColors.activeBg : undefined,
            borderColor: isRevamp && !embedded ? themeColors.primary : undefined,
          }}
          _expanded={embedded ? { bg: themeColors.activeBg } : undefined}
          _focus={embedded ? { boxShadow: 'none' } : undefined}
        >
          <Flex alignItems='center' gap={embedded ? 3 : undefined}>
            {embedded && (
              <PlanToolbarFilterIcon width={16} height={16} color={themeColors.primary} />
            )}

            <Text
              {...(isRevamp ? PLAN_TEXT.body : {})}
              size={isRevamp ? undefined : 'Roboto-12'}
              fontSize={isRevamp ? undefined : undefined}
              color={isRevamp ? themeColors.textDark : 'rgba(255, 255, 255, 0.6)'}
              mr={embedded ? 0 : 2}
              whiteSpace='nowrap'
              lineHeight={embedded ? '1.2' : undefined}
            >
              {isRevamp ? 'Filter by' : 'Filter by:'}
            </Text>

            {!embedded &&
              selectedBlockDimension &&
              blockDimensions?.dimensions &&
              indicatorfiltersName(selectedBlockDimension, blockDimensions.dimensions)?.map(
                (item: any, index: number) => (
                  <React.Fragment key={index}>
                    {item && (
                      <>
                        <Divider
                          mx={2}
                          orientation='vertical'
                          size='2xs'
                          colorScheme='grey'
                          height='12px'
                          alignSelf='center'
                        />
                        <Text
                          {...(isRevamp ? PLAN_TEXT.bodySemibold : {})}
                          size={isRevamp ? undefined : 'Roboto-12'}
                          fontSize={isRevamp ? undefined : undefined}
                          fontWeight={isRevamp ? undefined : undefined}
                          color={isRevamp ? themeColors.primary : undefined}
                          whiteSpace={'nowrap'}
                        >
                          {item}
                        </Text>
                      </>
                    )}
                  </React.Fragment>
                ),
              )}

            {isRevamp && activeFilterCount > 0 && (
              <Flex
                minW='18px'
                h='18px'
                px={1}
                ml={embedded ? 0 : 2}
                borderRadius='full'
                bg={badgeColor ?? themeColors.danger}
                color={themeColors.white}
                {...(isRevamp ? PLAN_TEXT.badge : {})}
                alignItems='center'
                justifyContent='center'
                flexShrink={0}
                fontSize={isRevamp ? undefined : '10px'}
              >
                {activeFilterCount}
              </Flex>
            )}

            {embedded ? (
              <PlanToolbarChevronDownIcon width={10} height={6} color={themeColors.primary} />
            ) : (
              <ChevronDownIcon
                boxSize={isRevamp ? 3 : undefined}
                color={isRevamp ? themeColors.primary : undefined}
              />
            )}
          </Flex>
        </MenuButton>
        {/* {isOpen && ( */}
        <MenuList zIndex={1500} minW={isRevamp ? '320px' : undefined}>
          <Flex
            gap={8}
            maxW={isMobile ? '300px' : '600px'}
            overflowX={'auto'}
            className='sm-scroll'
            pb={4}
          >
            {blockDimensions?.dimensions?.map((checkBoxItem: any, index: number) => (
              <React.Fragment key={index}>
                <MultiSelectCheckBox
                  clearButton={clearButton}
                  checkboxData={checkBoxItem}
                  isMenuOpen={isMenuOpen}
                />
              </React.Fragment>
            ))}
          </Flex>
          <Flex justifyContent={'space-between'} mt={4}>
            {blockDimensions?.dimensions?.length > 1 && (
              <Button variant={'clear_button'} onClick={handleClearAll}>
                Clear all
              </Button>
            )}
            <Flex>
              <Button variant={'cancel_button'} mr={3} onClick={() => setIsMenuOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleApplyFilter} variant={'save_button'}>
                Apply
              </Button>
            </Flex>
          </Flex>
        </MenuList>
      </Menu>
    </ErrorBoundary>
  );
};

export default MultipleIndicatorFilters;
